import { z } from 'zod'

export const FileRouteContextSchema = z.enum([
  'archive',
  'backup',
  'contacts',
  'trash',
  'shared',
  'sync',
  'photos',
  'search',
])
export const fileRouteRegEx = new RegExp(
  `^/web/(${FileRouteContextSchema.options.join('|')})`,
)
export type FileRouteContext = z.infer<typeof FileRouteContextSchema>
export function isFileRouteContext(value: unknown): value is FileRouteContext {
  return FileRouteContextSchema.safeParse(value).success
}
export const FileRouteViewModeSchema = z.enum(['list', 'thumbs'])
export type FileRouteViewMode = z.infer<typeof FileRouteViewModeSchema>
export const FileRouteSortBySchema = z.enum([
  'name',
  'name-desc',
  'modified',
  'modified-desc',
  'size',
  'size-desc',
])
export const FileRouteSearchQuerySchema = z.string()
export type FileRouteSortBy = z.infer<typeof FileRouteSortBySchema>
export const FileRouteSortByToSortColumnAndDir = {
  name: {
    dir: 'asc',
    name: 'name',
    current: 'name',
    next: 'name-desc',
  },
  'name-desc': {
    dir: 'desc',
    name: 'name',
    current: 'name-desc',
    next: 'name',
  },
  modified: {
    dir: 'asc',
    name: 'modified',
    current: 'modified',
    next: 'modified-desc',
  },
  'modified-desc': {
    dir: 'desc',
    name: 'modified',
    current: 'modified-desc',
    next: 'modified',
  },
  size: {
    dir: 'asc',
    name: 'size',
    current: 'size',
    next: 'size-desc',
  },
  'size-desc': {
    dir: 'desc',
    name: 'size',
    current: 'size-desc',
    next: 'size',
  },
} satisfies Record<
  FileRouteSortBy,
  {
    dir: 'asc' | 'desc'
    name: FileRouteSortByColumn
    current: FileRouteSortBy
    next: FileRouteSortBy
  }
>
export type FileRouteSortByColumn = 'name' | 'modified' | 'size'
export type FileRouteParams = z.infer<typeof FileRouteSchema>

export const defaultFileRouteParams: FileRouteParams = {
  context: 'sync',
  sortBy: 'name',
  viewMode: 'thumbs',
  query: undefined,
  '*': '',
}
export const FileRouteSchema = z
  .object({
    context: FileRouteContextSchema.default('sync'),
    viewMode: FileRouteViewModeSchema.default('list'),
    sortBy: FileRouteSortBySchema.default('name'),
    query: FileRouteSearchQuerySchema.optional(),
    '*': z.string().optional().default(''),
  })
  .transform(v => ({
    ...v,
    context: v.query !== undefined ? 'search' : v.context,
  }))
export const StrictFileRouteSchema = z.object({
  context: FileRouteContextSchema,
  viewMode: FileRouteViewModeSchema,
  sortBy: FileRouteSortBySchema,
  query: FileRouteSearchQuerySchema.optional(),
  '*': z.string().optional().default(''),
})

export const makeFileRouteCatchSchema = (
  defaults: Partial<typeof defaultFileRouteParams>,
) => {
  const catchObj = Object.assign({}, defaultFileRouteParams, defaults)

  return z
    .object({
      context: FileRouteContextSchema.catch(catchObj.context).default(
        catchObj.context,
      ),
      viewMode: FileRouteViewModeSchema.catch(catchObj.viewMode).default(
        catchObj.viewMode,
      ),
      sortBy: FileRouteSortBySchema.catch(catchObj.sortBy).default(
        catchObj.sortBy,
      ),
      query: FileRouteSearchQuerySchema.optional().catch(catchObj.query),
      '*': z.string().optional().catch(catchObj['*']).default(catchObj['*']),
    })
    .catch(catchObj)
    .transform(v => ({
      ...v,
      context: v.query !== undefined ? 'search' : v.context,
    }))
}
